import React, { Fragment, useState, useEffect, useRef } from "react";
import { useAuthentication, useDispatchService } from "services";
import {
  LoadHeader,
  HeaderBox,
  MsgBox,
  Stops,
  Totals,
  TitleHeader,
  Confirm,
  DueOut,
} from "components/load-confirmation";
import "./load-confirmation.scss";
import {
  faSpinner,
  faAlarmExclamation,
  faWrench,
} from "@fortawesome/pro-solid-svg-icons"; //more icons at: https://fontawesome.com/icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router";
import notify from "devextreme/ui/notify";
import {
  NonCompletedLoadsByDriverResponse,
  NonCompletedLoadsByDriverSummaryResponse,
} from "types/nonCompletedLoadsByDriverResponse";

let dateFormat = require("dateformat");
const FUEL_PLAN_FEATURE_KEY = "driver-fuel-playbook";

export default () => {
  const history = useHistory();

  const { getUserClaim } = useAuthentication();
  const [confirmations, setConfirmations] = useState<
    NonCompletedLoadsByDriverSummaryResponse[] | null
  >(null);
  const [previousConfirmations, setPreviousConfirmations] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [useOdsVersion, setUseOdsVersion] = useState<boolean>(false);
  const [isFuelPlanEligible, setIsFuelPlanEligible] = useState<boolean>(false);
  const fuelPlanEligibilityRequestId = useRef(0);
  let {
    getDriverConfirmations,
    confirmLoad,
    getDriverFeatureEligibility,
  } = useDispatchService();

  const loadFuelPlanEligibility = async (driverId: string): Promise<void> => {
    const requestId = ++fuelPlanEligibilityRequestId.current;
    setIsFuelPlanEligible(false);

    try {
      const isEligible = await getDriverFeatureEligibility(
        driverId,
        FUEL_PLAN_FEATURE_KEY
      );

      if (requestId === fuelPlanEligibilityRequestId.current) {
        setIsFuelPlanEligible(isEligible);
      }
    } catch (error) {
      if (requestId === fuelPlanEligibilityRequestId.current) {
        setIsFuelPlanEligible(false);
        console.warn("Fuel plan eligibility lookup failed.", error);
      }
    }
  };

  const getData = async () => {
    try {
      let driverId = await getUserClaim("driverId");

      if (driverId) {
        let stringValue = driverId.toString();
        void loadFuelPlanEligibility(stringValue);

        const response = await getDriverConfirmations(stringValue);

        const data = response.summaries;
        setUseOdsVersion(response.useOdsVersion);
        setConfirmations(data);
        refreshChanges(data);
        setPreviousConfirmations(data);
        setErrorMessage("");
      } else {
        setIsFuelPlanEligible(false);
      }
    } catch (error: any) {
      if (
        error.response != null &&
        error.response.data != null &&
        error.response.data.message != null
      ) {
        setErrorMessage("Error: " + error.response.data.message);
      } else {
        setErrorMessage(error.message);
      }
    }
  };

  function refreshChanges(
    confirmations: NonCompletedLoadsByDriverSummaryResponse[]
  ): void {
    // Refresh a graphic icon indicating a load changed.
    if (previousConfirmations != null && confirmations != null) {
      confirmations.map((confirmation: any, index: number) => {
        if (index < previousConfirmations.length) {
          if (
            previousConfirmations[index].changed == true ||
            previousConfirmations[index].stops.length !=
              confirmation.stops.length
          ) {
            confirmation.changed = true;
          }
        }
      });
      setConfirmations(confirmations);
    }
  }

  useEffect(() => {
    getData();

    return () => {
      fuelPlanEligibilityRequestId.current += 1;
    };
  }, []);

  const onConfirmClick = async (loadNumber: string) => {
    var result = await confirmLoad(loadNumber);
    if (result) {
      // Refresh the view.
      getData();
    } else {
      alert("Confirmation failed.");
    }
  };

  const checkForUpdate = async () => {
    setConfirmations(null);
    await getData();
  };
  const isFuelPlanAvailable = (dueOut: string, isInternational: boolean): boolean => {
    if (isInternational) {
      return false;
    }

    const dueOutTime = new Date(dueOut).getTime();

    if (Number.isNaN(dueOutTime)) {
      return false;
    }

    const fourHoursBeforeDueOut = dueOutTime - 4 * 60 * 60 * 1000;
    return Date.now() >= fourHoursBeforeDueOut;
  };

  const onFuelPlanClick = (
    loadNumber: string,
    dueOut: string,
    timeZone: string,
    isInternational: boolean
  ) => {
    if (!isFuelPlanEligible) {
      return;
    }

    if (isInternational) {
      notify("Fuel plans are unavailable for international loads.", "info", 4000);
      return;
    }

    if (!isFuelPlanAvailable(dueOut, isInternational)) {
      notify(`Fuel Plan ${getFuelPlanAvailableMessage(dueOut, timeZone).toLowerCase()}.`, "info", 4000);
      return;
    }

    history.push(`/fuel-plan?loadNumber=${loadNumber}`);
  };
  const getFuelPlanAvailableMessage = (
    dueOut: string,
    timeZone: string
  ): string => {
    const dueOutTime = new Date(dueOut).getTime();

    if (Number.isNaN(dueOutTime)) {
      return "Available 4 hours before due out";
    }

    const availableDate = new Date(dueOutTime - 4 * 60 * 60 * 1000);
    return `Available ${dateFormat(
      availableDate,
      "mm/dd - HH:MM"
    )} ${timeZone}`;
  };
  return (
    <Fragment>
      <div className={"tap-to-refresh"} onClick={checkForUpdate}>
        Tap to Refresh
      </div>
      <hr />
      <div className="data-source-indicator">
        {useOdsVersion ? "(ODS)" : "(TMW)"}
      </div>
      {isFuelPlanEligible && (
        <aside className="fuel-plan-beta-banner" role="note">
          <p>
            You've been chosen to take part in Fuel Plan beta testing! Thank you
            for using this feature and helping us improve it.
          </p>
        </aside>
      )}

      {confirmations ? (
        confirmations.length > 0 ? (
          <div>
            {confirmations.map(
              (
                confirmation: NonCompletedLoadsByDriverSummaryResponse,
                index: number
              ) => (
                <div className="bottomPadded">
                  <div className={`main-container-item-container`}>
                    <HeaderBox
                      headerTitle={confirmation.confirmationState}
                      changed={confirmation.changed ?? false}
                      displayStatus={confirmation.statusToDisplay}
                      isLoadReady={confirmation.isLoadReady}
                    />
                    <div className={"inside-card"}>
                      <LoadHeader
                        trailer={confirmation.trailerNumber}
                        load={confirmation.loadNumber}
                        truck={confirmation.truckNumber}
                        driverTwoId={confirmation.driverTwoNumber}
                        confirmationState={confirmation.confirmationState}
                      />
                      <div className="due-out-fuel-plan-row">
                        <div className="due-out-fuel-plan-line">
                          <DueOut
                            message={confirmation.dueOut}
                            timeZone={confirmation.dueOutTimeZone}
                          />
                        </div>
                        {isFuelPlanEligible && (
                          <div className="fuel-plan-inline-action">
                            <button
                              aria-disabled={!isFuelPlanAvailable(confirmation.dueOut, confirmation.isInternational)}
                              className={`fuel-plan-inline-button ${!isFuelPlanAvailable(confirmation.dueOut, confirmation.isInternational) ? "fuel-plan-inline-button-unavailable" : ""}`}
                              onClick={() =>
                                onFuelPlanClick(
                                  confirmation.loadNumber,
                                  confirmation.dueOut,
                                  confirmation.dueOutTimeZone,
                                  confirmation.isInternational
                                )
                              }
                              title={
                                confirmation.isInternational
                                  ? "Fuel plans are unavailable for international loads"
                                  : isFuelPlanAvailable(confirmation.dueOut, confirmation.isInternational)
                                  ? "Open fuel plan"
                                  : getFuelPlanAvailableMessage(
                                      confirmation.dueOut,
                                      confirmation.dueOutTimeZone
                                    )
                              }
                              type="button"
                            >
                              Fuel Plan
                            </button>
                          </div>
                        )}
                      </div>
                      <MsgBox message={confirmation.comment} />
                      <Stops
                        loadNumber={confirmation.loadNumber}
                        stops={confirmation.stops}
                        useOdsVersion={useOdsVersion}
                      />

                      <div className="miles-row">
                        <span className="miles-label">Miles:</span>
                        <span>{confirmation.miles}</span>
                      </div>
                      <div className="dividerline-margins" />

                      <TitleHeader title="Totals" />
                      <Totals
                        weight={confirmation.weightTotal}
                        cube={confirmation.cubeTotal}
                        pieceCount={confirmation.piecesTotal}
                        items={confirmation.lineItems}
                      />

                      <div className="preferred-route-button-div">
                        <span className="paddedSpan">
                          {confirmation.confirmationState == "Unconfirmed" &&
                            !useOdsVersion && (
                              <Confirm
                                loadNumber={confirmation.loadNumber}
                                onConfirmClick={onConfirmClick}
                              />
                            )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="center-icon center full-height">
            <FontAwesomeIcon icon={faWrench} size={"3x"} />
            <div>
              <br />
              You have no active loads assigned.
              <br />
              <button onClick={() => history.push("/demo-load")}>
                {" "}
                Click here for demo load
              </button>
            </div>
          </div>
        )
      ) : errorMessage.length > 0 ? (
        <div className="center-icon center full-height red-component">
          <FontAwesomeIcon icon={faAlarmExclamation} size={"3x"} />
          <div>
            <br />
            {errorMessage}
          </div>
        </div>
      ) : (
        <div className="center-icon center full-height red-component fas fa-spinner fa-spin">
          <FontAwesomeIcon icon={faSpinner} size={"3x"} />
        </div>
      )}
    </Fragment>
  );
};
